class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    
    // Get unique identifier for this instance
    this.searchId = this.dataset.searchId || 'default';
    
    // Use instance-specific classes
    this.drawerClass = "wt-drawer-search";
    this.classDrawerActive = `wt-drawer-search--active`;
    this.instanceActiveClass = `wt-drawer-search--${this.searchId}-active`;
    
    // Instance-specific overlay class
    this.activeOverlayBodyClass = `search-overlay-on-${this.searchId}`;
    
    this.body = document.body;

    // search elements - all scoped to THIS component
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchProperties = this.dataset.searchProperties;
    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.searchResultTop = this.querySelector(".search-result-top");
    this.form = this.querySelector("form.store-search-form");

    this.isVisibleClearButton = false;
    this.toggleTabindexElements = [this.input, this.closeButton];
    this.suggestionMenuContent = this.searchResultTop ? this.searchResultTop.cloneNode(true) : null;
    this.timeOut = null;

    this.init();
  }

  // Local setTabindex method
  setTabindex(elements, value) {
    if (!elements) return;
    elements.forEach((el) => {
      if (el) el.setAttribute("tabindex", value);
    });
  }

  getFocusableElements() {
    const focusableElementsSelector = "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.setAttribute("open", "");
    this.classList.add(this.classDrawerActive);
    this.classList.add(this.instanceActiveClass);
    this.body.classList.add(this.activeOverlayBodyClass);
    this.body.classList.add('search-overlay-on'); // Generic class for shared styles
    
    this.setTabindex(this.toggleTabindexElements, "0");
    this.setTabindex([this.mainTrigger], "-1");
    
    if (this.input) {
      this.input.focus();
      this.input.setSelectionRange(this.input.value.length, this.input.value.length);
    }
    this.isOpen = true;
  }

  close() {
    this.removeAttribute("open");
    this.classList.remove(this.classDrawerActive);
    this.classList.remove(this.instanceActiveClass);
    this.body.classList.remove(this.activeOverlayBodyClass);
    this.body.classList.remove('search-overlay-on');
    
    this.setTabindex(this.toggleTabindexElements, "-1");
    this.setTabindex([this.mainTrigger], "0");
    
    this.isOpen = false;
    
    setTimeout(() => {
      if (this.mainTrigger) this.mainTrigger.focus();
    }, 0);
  }

  init() {
    const isPredictiveSearchAvailable = this.checkPredictiveSearch();
    if (!isPredictiveSearchAvailable) return;

    // Trigger click handler - scoped to this component's trigger
    if (this.mainTrigger) {
      this.mainTrigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onToggle();
      });
    }

    // Close button handler
    if (this.closeButton) {
      this.closeButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Clear button handler
    if (this.clearButton) {
      this.clearButton.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.input) this.input.value = "";
        this.clearButton.style.display = "none";
        this.isVisibleClearButton = false;
        this.clearResults();
      });
    }

    // Escape key handler
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        if (this.isOpen) {
          e.preventDefault();
          this.close();
        }
      }

      // Tab trap
      const isTabPressed = e.key === "Tab" || e.keyCode === 9;
      if (isTabPressed && this.isOpen) {
        const { first, last } = this.getFocusableElements();
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    // Input handlers
    if (this.input) {
      // Show/hide clear button
      this.input.addEventListener("input", () => {
        if (this.input.value.length > 0 && !this.isVisibleClearButton) {
          this.clearButton.style.display = "flex";
          this.clearButton.setAttribute("tabindex", "0");
          this.isVisibleClearButton = true;
        } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
          this.clearButton.style.display = "none";
          this.clearButton.setAttribute("tabindex", "-1");
          this.isVisibleClearButton = false;
        }
      });

      // Debounced search
      this.input.addEventListener("input", debounce(() => {
        this.onChange();
      }, 300));
    }

    // Form submit handler
    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
          e.preventDefault();
        }
      });
    }

    // Click outside to close
    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.contains(e.target)) {
        this.close();
      }
    });

    // Shopify design mode
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        if (this.isOpen) this.close();
      });
    }
  }

  checkPredictiveSearch() {
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      try {
        const featuresData = JSON.parse(featuresTag.textContent);
        if (featuresData.predictiveSearch === false) {
          const searchPageUrl = this.dataset.searchPageUrl;
          if (this.mainTrigger) {
            this.mainTrigger.addEventListener("click", (e) => {
              e.preventDefault();
              window.location.href = searchPageUrl;
            });
          }
          return false;
        }
      } catch (e) {
        console.error("Error parsing Shopify features:", e);
      }
    }
    return true;
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&${encodeURIComponent("resources[options][fields]")}=${encodeURIComponent(this.searchProperties)}&section_id=predictive-search`;

    fetch(searchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search")?.innerHTML;
        
        if (resultsMarkup) {
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        console.error("Search error:", error);
        this.removeAttribute("loading");
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute("results", true);
    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  clearResults() {
    if (this.input) this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    
    if (this.statusElement && this.suggestionMenuContent) {
      this.statusElement.innerHTML = this.suggestionMenuContent.innerHTML || "";
    }

    clearTimeout(this.timeOut);
    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.removeAttribute("aria-hidden");
      }
    }, 300);
  }
}

customElements.define("search-drawer", DrawerSearchSection);